"use client";

import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { ChevronDown, ChevronRight, Loader2 } from "lucide-react";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  uploadAddressProofRequest,
  updateKycFieldsRequest,
} from "@/store/slices/kyc.slice";
import { formDataUtils } from "@/services/kycService";
import { getUpdatableFieldsFromProblems, KYCData } from "@/types/models/kyc";
import { store } from "@/store/store";
import FileUpload from "./FileUpload";

// Validation Schema
const addressVerificationSchema = z.object({
  address_proof_file: z
    .any()
    .optional()
    .refine(
      (file) => !file || file instanceof File,
      "Address proof document must be a valid file",
    ),
});

type AddressVerificationFormData = {
  address_proof_file?: File;
};

interface AddressVerificationStepProps {
  onPrevious: () => void;
  storeId: string;
  kycData?: KYCData | null;
}

export const AddressVerificationStep: React.FC<
  AddressVerificationStepProps
> = ({ onPrevious, storeId, kycData = null }) => {
  const dispatch = useAppDispatch();
  const { isLoading, error, isResubmissionMode, rejectedSteps, kycProblems } =
    useAppSelector((state) => state.kyc);

  const [addressProofFile, setAddressProofFile] = useState<
    File | string | null
  >(kycData?.address_proof_document || null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  
  // Track if form has been submitted to prevent resetting user input on errors
  const [hasSubmitted, setHasSubmitted] = useState<boolean>(false);

  const {
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<AddressVerificationFormData>({
    resolver: zodResolver(addressVerificationSchema),
  });

  // Update form values when file changes
  React.useEffect(() => {
    if (addressProofFile) {
      // If the value is a remote URL string we don't set a File object, but we keep preview state
      setValue("address_proof_file", addressProofFile as any);
    }
  }, [addressProofFile, setValue]);

  // Initialize file from kycData only on first load, not after user interactions
  React.useEffect(() => {
    if (kycData?.address_proof_document && !hasSubmitted && !addressProofFile) {
      setAddressProofFile(kycData.address_proof_document);
    }
  }, [kycData, hasSubmitted, addressProofFile]);

  const onSubmit = async () => {
    setSubmitError(null);
    
    // Mark that user has attempted submission to prevent form resets
    setHasSubmitted(true);

    if (!addressProofFile) {
      setSubmitError("Please upload address proof document");
      return;
    }

    try {
      // Use PATCH endpoint for resubmissions, POST for initial submissions
      if (isResubmissionMode && rejectedSteps?.addressVerification) {
        // PATCH: Update only the fields that were actually rejected
        const updatableFields = getUpdatableFieldsFromProblems(
          kycProblems || [],
        );
        const fields: Record<string, unknown> = {};

        // Only include address proof if it was marked as problematic and is a File
        if (
          updatableFields.includes("address_proof_document") &&
          addressProofFile &&
          typeof addressProofFile !== "string"
        ) {
          fields.address_proof_document = addressProofFile;
        }

        // Ensure we have at least one field to update
        if (Object.keys(fields).length === 0) {
          setSubmitError(
            "No problematic address proof found to update. Please contact support.",
          );
          return;
        }

        await new Promise((resolve, reject) => {
          const unsubscribe = store.subscribe(() => {
            const state = store.getState();
            if (!state.kyc.isLoading) {
              unsubscribe();
              if (state.kyc.error) {
                reject(new Error(state.kyc.error));
              } else {
                resolve(true);
              }
            }
          });

          dispatch(
            updateKycFieldsRequest({
              storeId,
              fields,
            }),
          );
        });
      } else {
        // POST: Initial address proof upload
        const payload: Record<string, File> = {};
        if (addressProofFile && typeof addressProofFile !== "string") {
          payload.address_proof_document = addressProofFile;
        }

        const formData = formDataUtils.createAddressVerificationFormData(
          payload as Parameters<
            typeof formDataUtils.createAddressVerificationFormData
          >[0],
        );

        await new Promise((resolve, reject) => {
          const unsubscribe = store.subscribe(() => {
            const state = store.getState();
            if (!state.kyc.isLoading) {
              unsubscribe();
              if (state.kyc.error) {
                reject(new Error(state.kyc.error));
              } else if (state.kyc.completedSteps.addressVerification) {
                resolve(true);
              }
            }
          });

          dispatch(
            uploadAddressProofRequest({
              storeId,
              formData,
            }),
          );
        });
      }

      // Success - Redux will automatically advance to the next step
      console.log(
        "[AddressVerificationStep] Address proof uploaded successfully, Redux will handle step advancement",
      );
    } catch (error: unknown) {
      setSubmitError(
        error instanceof Error
          ? error.message
          : "Failed to upload address proof. Please try again.",
      );
    }
  };

  const acceptedDocuments = [
    "Utility Bill (Electricity, Water, Gas)",
    "Bank Statement",
    "Rent Agreement",
    "Property Tax Receipt",
    "Telephone Bill",
    "Internet Bill",
    "Municipal Corporation Certificate",
  ];

  return (
    <div className="w-full max-w-4xl mx-auto">
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-white mb-2">
          Address Verification
        </h2>
        <p className="text-auth-text-muted">
          Upload a document that proves your business address. The document
          should be recent (within 3 months).
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Address Proof Upload */}
        <FileUpload
          onFileSelect={setAddressProofFile}
          accept="image/jpeg,image/png"
          maxSize={2 * 1024 * 1024} // 2MB
          fileType="image"
          label="Address Proof Document"
          description="Upload a clear photo of your address proof document (JPEG/PNG, max 2MB)"
          required
          currentFile={addressProofFile}
          error={errors.address_proof_file?.message}
          isUploading={isLoading}
        />

        {/* Accepted Documents */}
        <div className="bg-auth-card border border-auth-border rounded-2xl p-4">
          <h3 className="text-white font-medium mb-3">
            Accepted Address Proof Documents:
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {acceptedDocuments.map((doc, index) => (
              <div key={index} className="flex items-center space-x-2">
                <div className="w-1.5 h-1.5 bg-brand rounded-full flex-shrink-0" />
                <span className="text-auth-text-muted text-sm">{doc}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Requirements */}
        <div className="bg-auth-card border border-auth-border rounded-2xl p-4">
          <h3 className="text-white font-medium mb-2">
            Document Requirements:
          </h3>
          <ul className="text-auth-text-muted text-sm space-y-1">
            <li>• Document should be issued within the last 3 months</li>
            <li>• Your name and address should be clearly visible</li>
            <li>
              • Document should match the business address provided earlier
            </li>
            <li>• Ensure the document is well-lit and all text is readable</li>
            <li>• File formats: JPEG or PNG only</li>
            <li>• Maximum file size: 2MB</li>
          </ul>
        </div>

        {/* Error Message */}
        {(error || submitError) && (
          <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
            <p className="text-red-500 text-sm">{error || submitError}</p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-between items-center pt-6">
          <button
            type="button"
            onClick={onPrevious}
            className="flex items-center space-x-2 px-6 py-3 bg-muted/50 border border-border rounded-[30px] text-muted-foreground hover:text-foreground hover:bg-muted hover:border-muted-foreground/50 transition-all duration-200 shadow-sm hover:shadow-md"
          >
            <ChevronDown className="w-5 h-5 rotate-90" />
            <span>Previous</span>
          </button>

          <button
            type="submit"
            disabled={isLoading || !addressProofFile}
            className="flex items-center space-x-2 px-6 py-3 bg-primary text-primary-foreground font-semibold hover:bg-primary/90 active:bg-primary/80 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl rounded-[30px] transform hover:scale-105 active:scale-95"
          >
            {isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
            <span>{isLoading ? "Uploading..." : "Next"}</span>
            {!isLoading && <ChevronRight className="w-5 h-5" />}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddressVerificationStep;
